import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Ruler, Tag } from "lucide-react";

// Sized pricing (one price-driving dimension) + optional no-price dimensions (e.g. Color).
export function VariantEditor({ value, onChange }) {
  const opts = value || [];
  const priceOpt = opts.find((o) => o.price_driving) || null;
  const others = opts.filter((o) => !o.price_driving);

  const commit = (price, nonPrice) => {
    const next = [];
    if (price && (price.values || []).length) next.push(price);
    (nonPrice || []).forEach((o) => next.push(o));
    onChange(next);
  };
  const pv = priceOpt ? priceOpt.values || [] : [];

  const setSizeLabel = (name) => commit({ ...(priceOpt || { price_driving: true, values: [] }), name, price_driving: true }, others);
  const addSize = () => commit({ name: (priceOpt && priceOpt.name) || "Size", price_driving: true, values: [...pv, { name: "", price: "" }] }, others);
  const setSize = (i, patch) => commit({ ...(priceOpt || { name: "Size", price_driving: true }), values: pv.map((v, j) => (j === i ? { ...v, ...patch } : v)) }, others);
  const removeSize = (i) => { const nv = pv.filter((_, j) => j !== i); commit(nv.length ? { ...priceOpt, values: nv } : null, others); };

  const setOthers = (arr) => commit(pv.length ? priceOpt : null, arr);
  const addOther = () => setOthers([...others, { name: "", price_driving: false, values: [] }]);
  const setOther = (i, patch) => setOthers(others.map((o, j) => (j === i ? { ...o, ...patch } : o)));
  const removeOther = (i) => setOthers(others.filter((_, j) => j !== i));
  const setOtherValues = (i, str) => setOther(i, { values: str.split(",").map((s) => ({ name: s.trim() })).filter((v) => v.name) });

  return (
    <div className="space-y-5" data-testid="variant-editor">
      <div>
        <div className="flex items-center gap-2 mb-1"><Ruler className="h-4 w-4 text-primary" /><Label className="text-sm font-semibold">Sizes with set prices</Label></div>
        <p className="text-xs text-muted-foreground mb-2">Give each size its own price (e.g. Small $3, Medium $4.50, Large $5). The storefront shows <b>"from $3"</b> and asks the customer to pick a size. Leave empty for a single-price product.</p>
        {pv.length > 0 && (
          <div className="flex items-center gap-2 mb-2">
            <Label className="text-xs text-muted-foreground w-24">Dimension name</Label>
            <Input className="rounded-none h-8 max-w-[160px]" value={priceOpt?.name || "Size"} onChange={(e) => setSizeLabel(e.target.value)} placeholder="Size" data-testid="variant-size-label" />
          </div>
        )}
        <div className="space-y-2">
          {pv.map((v, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`variant-size-row-${i}`}>
              <Input className="rounded-none flex-1" value={v.name} onChange={(e) => setSize(i, { name: e.target.value })} placeholder="Small" data-testid={`variant-size-name-${i}`} />
              <div className="relative w-28">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                <Input type="number" step="0.01" className="rounded-none pl-5" value={v.price} onChange={(e) => setSize(i, { price: e.target.value })} placeholder="0.00" data-testid={`variant-size-price-${i}`} />
              </div>
              <Button type="button" variant="ghost" size="icon" className="rounded-none text-destructive shrink-0" onClick={() => removeSize(i)} data-testid={`variant-size-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
        <Button type="button" variant="outline" size="sm" className="rounded-none mt-2 gap-1.5" onClick={addSize} data-testid="variant-add-size"><Plus className="h-4 w-4" /> Add size</Button>
      </div>

      <div className="border-t border-border pt-4">
        <div className="flex items-center gap-2 mb-1"><Tag className="h-4 w-4 text-primary" /><Label className="text-sm font-semibold">Other options <span className="text-muted-foreground normal-case font-normal">(no price change)</span></Label></div>
        <p className="text-xs text-muted-foreground mb-2">Selectable choices that don't change the price — e.g. <b>Color: Red, Blue, Green</b>. For paid add-ons, use Modifiers instead.</p>
        <div className="space-y-3">
          {others.map((o, i) => (
            <div key={i} className="border border-border p-2 space-y-2" data-testid={`variant-other-${i}`}>
              <div className="flex items-center gap-2">
                <Input className="rounded-none h-8 max-w-[160px]" value={o.name} onChange={(e) => setOther(i, { name: e.target.value })} placeholder="Color" data-testid={`variant-other-name-${i}`} />
                <Button type="button" variant="ghost" size="icon" className="rounded-none text-destructive ml-auto" onClick={() => removeOther(i)} data-testid={`variant-other-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
              <Input className="rounded-none" defaultValue={(o.values || []).map((v) => v.name).join(", ")} onBlur={(e) => setOtherValues(i, e.target.value)} placeholder="Red, Blue, Green (comma separated)" data-testid={`variant-other-values-${i}`} />
            </div>
          ))}
        </div>
        <Button type="button" variant="outline" size="sm" className="rounded-none mt-2 gap-1.5" onClick={addOther} data-testid="variant-add-other"><Plus className="h-4 w-4" /> Add option</Button>
      </div>
    </div>
  );
}
